import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Icon } from '@components/icons';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import { normalizeUrl } from '@utils/url';
import IframeModal from '@components/iframe-modal';

const StyledCard = styled.article`
  ${({ theme }) => theme.mixins.boxShadow};
  background-color: var(--light-navy);
  border-radius: var(--border-radius);
  padding: 2rem;
  width: 100%;

  header {
    ${({ theme }) => theme.mixins.flexBetween};
    align-items: flex-start;
    gap: 15px;
    margin-bottom: 12px;
  }

  h3 {
    margin: 0;
    color: var(--lightest-slate);
    font-size: clamp(22px, 4vw, 28px);
    line-height: 1.2;
  }

  .links {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 10px;
    color: var(--light-slate);

    a,
    span {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      font-family: var(--font-mono);
      font-size: var(--fz-xxs);
      padding: 8px 12px;
      border-radius: 999px;
      border: 1px solid var(--lightest-navy);
      background-color: rgba(2, 12, 27, 0.25);
      white-space: nowrap;
    }

    a {
      color: var(--green);

      &:hover,
      &:focus {
        background-color: rgba(100, 255, 218, 0.08);
        border-color: var(--green);
      }
    }

    .status {
      color: var(--slate);
    }

    svg {
      width: 16px;
      height: 16px;
    }
  }

  .play-link {
    color: var(--green);
    background: transparent;
    border: 0;
    padding: 0;
    margin-top: 14px;
    display: inline-flex;
    align-items: center;
    cursor: pointer;
    text-decoration: none;

    &:hover,
    &:focus-visible {
      text-decoration: underline;
      text-underline-offset: 3px;
    }
  }

  @media (max-width: 1023px) {
    .tech {
      display: none;
    }
  }

  .frame {
    margin-top: 18px;
    border-radius: var(--border-radius);
    overflow: hidden;
    background-color: var(--navy);
    position: relative;
  }

  @media (max-width: 480px) {
    padding: 1.5rem;

    .frame {
      padding: 10px;
    }

    .preview {
      border-radius: calc(var(--border-radius) - 2px);
      overflow: hidden;
    }
  }

  .placeholder {
    width: 100%;
    aspect-ratio: 16 / 9;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 16px;
    color: var(--lightest-slate);
    background: linear-gradient(135deg, rgba(100, 255, 218, 0.12), rgba(2, 12, 27, 0.9));
    border: 1px solid rgba(100, 255, 218, 0.2);

    span {
      display: block;
      color: var(--light-slate);
      margin-top: 6px;
      font-family: var(--font-mono);
      font-size: var(--fz-xxs);
    }
  }

  .overlay {
    position: absolute;
    inset: 0;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
    background: rgba(2, 12, 27, 0.68);
    backdrop-filter: blur(4px);
  }

  .mobile-tip {
    margin: 12px 0 0;
    color: var(--light-slate);
    font-size: var(--fz-xs);
    line-height: 1.4;
  }

  .overlay-inner {
    width: 100%;
    max-width: 520px;
    text-align: center;
    padding: 20px 18px;
    border-radius: var(--border-radius);
    background: rgba(2, 12, 27, 0.86);
    border: 1px solid rgba(100, 255, 218, 0.18);
  }

  .overlay-title {
    margin: 0 0 10px;
    color: var(--lightest-slate);
    font-size: clamp(18px, 3vw, 24px);
  }

  .overlay-desc {
    margin: 0 0 18px;
    color: var(--light-slate);
  }

  .overlay-actions {
    display: flex;
    gap: 12px;
    justify-content: center;
    flex-wrap: wrap;
  }

  .overlay-yes {
    ${({ theme }) => theme.mixins.smallButton};
  }

  .preview {
    position: relative;
    width: 100%;
    aspect-ratio: 16 / 9;
    max-height: 260px;
    background-color: var(--navy);

    .img {
      width: 100%;
      height: 100%;
    }

    .img img {
      object-fit: cover !important;
    }
  }

  .tech {
    ${({ theme }) => theme.mixins.resetList};
    display: flex;
    flex-wrap: wrap;
    gap: 10px 15px;
    margin-top: 18px;
    color: var(--slate);

    li {
      font-family: var(--font-mono);
      font-size: var(--fz-xxs);
    }
  }
`;

const GameEmbed = ({ title, storyHtml, embedUrl, externalUrl, tech, cover, onReadAbout }) => {
  const resolvedExternal = normalizeUrl(externalUrl || embedUrl);
  const resolvedEmbed = normalizeUrl(embedUrl || externalUrl);
  const coverImage = useMemo(() => (cover ? getImage(cover) : null), [cover]);
  const [isGameOpen, setIsGameOpen] = useState(false);
  const [canShowEmbedOverlay, setCanShowEmbedOverlay] = useState(false);
  const canReadAbout = typeof storyHtml === 'string' && storyHtml.trim().length > 0;

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const mql = window.matchMedia('(min-width: 481px)');
    const onChange = e => setCanShowEmbedOverlay(Boolean(e.matches));
    setCanShowEmbedOverlay(Boolean(mql.matches));

    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    }

    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, []);

  return (
    <StyledCard>
      <header>
        <h3>{title}</h3>
        <div className="links">
          {resolvedExternal && (
            <a
              href={resolvedExternal}
              aria-label={`View ${title} project`}
              target="_blank"
              rel="noreferrer">
              View project <Icon name="External" />
            </a>
          )}
        </div>
      </header>

      {canReadAbout && typeof onReadAbout === 'function' && (
        <button
          className="play-link"
          type="button"
          onClick={() => onReadAbout({ title, html: storyHtml })}
          aria-label={`Read about ${title}`}>
          Read about project
        </button>
      )}

      {resolvedEmbed && (
        <div className="frame" aria-label={`${title} game embed`}>
          <div className="preview">
            {coverImage ? (
              <GatsbyImage image={coverImage} alt={`${title} preview`} className="img" />
            ) : (
              <div className="placeholder">
                <div>
                  {title} (preview)
                  <span>Play in an embedded window</span>
                </div>
              </div>
            )}
            {canShowEmbedOverlay && (
              <div className="overlay" role="dialog" aria-label={`Play ${title} prompt`}>
                <div className="overlay-inner">
                  <h4 className="overlay-title">Do you want to play?</h4>
                  <p className="overlay-desc">
                    This will load the game inside an embedded frame. You can also open it in a new
                    tab.
                  </p>
                  <div className="overlay-actions">
                    <button
                      className="overlay-yes"
                      type="button"
                      onClick={() => setIsGameOpen(true)}>
                      Yes, play
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {!canShowEmbedOverlay && (
            <p className="mobile-tip">
              Tip: To play the embedded version, switch to “Desktop site” on mobile or use a larger
              screen.
            </p>
          )}
        </div>
      )}

      <IframeModal
        isOpen={isGameOpen}
        title={title}
        ariaLabel={`Play ${title}`}
        onClose={() => setIsGameOpen(false)}
        src={isGameOpen ? resolvedEmbed : undefined}
      />

      {tech?.length > 0 && (
        <ul className="tech">
          {tech.map((t, i) => (
            <li key={i}>{t}</li>
          ))}
        </ul>
      )}
    </StyledCard>
  );
};

GameEmbed.propTypes = {
  title: PropTypes.string.isRequired,
  storyHtml: PropTypes.string,
  embedUrl: PropTypes.string,
  externalUrl: PropTypes.string,
  tech: PropTypes.arrayOf(PropTypes.string),
  cover: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  onReadAbout: PropTypes.func,
};

export default GameEmbed;
